import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { COMPANY, LOGO_URL_LIGHT } from "@/data/content";
import { useT, useRoute } from "@/i18n";

export default function Footer() {
  const t = useT();
  const route = useRoute();

  const col = [
    {
      title: t.footer.columns.about,
      links: [{ label: t.footer.links.about, to: route("about") }],
    },
    {
      title: t.footer.columns.support,
      links: [
        { label: t.footer.links.imprint, to: route("imprint") },
        { label: t.footer.links.privacy, to: route("privacy") },
        { label: t.footer.links.contact, to: route("contact") },
        { label: t.footer.links.faq, to: route("faq") },
      ],
    },
  ];

  return (
    <footer data-testid="site-footer" className="bg-black text-white">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 pt-24 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* Brand */}
          <div>
            <Link to={route("home")} className="inline-flex items-center gap-3" data-testid="footer-logo">
              <img src={LOGO_URL_LIGHT} alt="Artful" className="h-[68px] w-auto" draggable="false" />
              <span className="text-[17px] font-bold uppercase tracking-[0.3em] text-white/80 border-l border-white/15 pl-3">
                Contemporary
              </span>
            </Link>
            <p className="mt-6 text-sm text-white leading-relaxed max-w-xs">{t.footer.tagline}</p>
            <div className="mt-6 space-y-2 text-sm text-white">
              <p className="flex items-start gap-2">
                <MapPin size={14} className="mt-1 text-[#C20E1A]" /> {COMPANY.address}
              </p>
              <p className="flex items-center gap-2">
                <Phone size={14} className="text-[#C20E1A]" /> {COMPANY.phone}
              </p>
              <p className="flex items-center gap-2">
                <Mail size={14} className="text-[#C20E1A]" />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-white">
                  {COMPANY.email}
                </a>
              </p>
            </div>
          </div>

          {col.map((c) => (
            <div key={c.title}>
              <h4 className="text-[11px] uppercase tracking-[0.3em] text-[#C20E1A] font-bold mb-6">
                {c.title}
              </h4>
              <ul className="space-y-3">
                {c.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={`footer-${l.to.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                      className="text-sm text-white hover:text-[#C20E1A] transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="text-[11px] uppercase tracking-[0.3em] text-[#C20E1A] font-bold mb-6">
              {t.footer.columns.follow}
            </h4>
            <div className="flex items-center gap-3">
              <a
                href={COMPANY.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="footer-facebook"
                className="h-10 w-10 inline-flex items-center justify-center border border-white/15 hover:bg-[#C20E1A] hover:border-[#C20E1A] transition-colors"
              >
                <Facebook size={16} />
              </a>
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram"
                className="h-10 w-10 inline-flex items-center justify-center border border-white/15 hover:bg-[#C20E1A] hover:border-[#C20E1A] transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href={COMPANY.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
                className="h-10 w-10 inline-flex items-center justify-center border border-white/15 hover:bg-[#C20E1A] hover:border-[#C20E1A] transition-colors"
              >
                <Linkedin size={16} />
              </a>
            </div>
            <p className="mt-8 text-xs text-white">{t.footer.closing}</p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white">
            © {new Date().getFullYear()} Artful Contemporary. {t.footer.copyright}
          </p>
          <p className="text-xs text-white uppercase tracking-[0.3em]">{t.footer.crafted}</p>
        </div>
      </div>
    </footer>
  );
}
